import {
  ArrowUpRight,
  BookOpen,
  Calendar,
  FileText,
  Heart,
  PlayCircle,
  Shapes,
  Table2,
  User,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Card } from "@/components/ui/card";
import { thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import type { DirectoryTool, ToolType } from "../../types";

/** Per-type glyph for the card's icon tile (parity with the expert monogram). */
const TYPE_ICON: Record<ToolType, LucideIcon> = {
  guide: BookOpen,
  action_sheet: FileText,
  video: PlayCircle,
  analysis_grid: Table2,
  other: Shapes,
};

/**
 * Directory result card for a tool (OAC-440): leads with a tool-type icon tile,
 * then the title, author + publication date, the type, and a summary kept
 * hidden behind a "Voir plus / Voir moins" disclosure so the directory stays
 * scannable. The "Accéder à l'outil" link and the favorite toggle are the two
 * actions (étapes 7-8).
 *
 * The disclosure is a native `<details>`, matching the Django card: the summary
 * stays in the document while hidden, so find-in-page reaches it (the browser
 * opens the element to reveal a match) instead of skipping over every result.
 */
export function ToolCard({
  tool,
  onToggleFavorite,
  isToggling = false,
}: {
  tool: DirectoryTool;
  onToggleFavorite: (tool: DirectoryTool) => void;
  isToggling?: boolean;
}) {
  const { t } = useTranslation();
  const TypeIcon = TYPE_ICON[tool.tool_type] ?? Shapes;
  // Tint the tile with the economic theme — the SPA's tool accent colour.
  const tile = thematicClasses("economic");

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex gap-3">
        <div
          className={cn(
            "grid size-11 shrink-0 place-items-center rounded-xl",
            tile.softBg,
            tile.ink,
          )}
          aria-hidden
        >
          <TypeIcon className="size-5" />
        </div>
        <div className="min-w-0 space-y-1">
          <p className="font-semibold leading-tight">{tool.title}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <User className="size-3.5 shrink-0" aria-hidden />
              {t("tools.card.by", { author: tool.author })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="size-3.5 shrink-0" aria-hidden />
              {tool.publication_year}
            </span>
            {tool.page_chapter && (
              <span className="inline-flex items-center gap-1.5">
                <BookOpen className="size-3.5 shrink-0" aria-hidden />
                {tool.page_chapter}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
          {tool.tool_type_display}
        </span>
      </div>

      {tool.summary && (
        // Collapsed, the label stands alone between the type badge and the
        // access row; the card's own gap plus this padding keep it from reading
        // as part of either.
        <details className="group/summary py-0.5 text-sm">
          <summary className="cursor-pointer list-none font-semibold text-theme-economic-ink underline-offset-4 hover:underline [&::-webkit-details-marker]:hidden">
            <span className="group-open/summary:hidden">
              {t("tools.card.seeMore")} +
            </span>
            <span className="hidden group-open/summary:inline">
              {t("tools.card.seeLess")}
            </span>
          </summary>
          <p className="mt-2 whitespace-pre-line text-muted-foreground">
            {tool.summary}
          </p>
        </details>
      )}

      <div className="mt-auto flex items-center justify-between border-t border-border/70 pt-3">
        <a
          href={tool.access_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm font-semibold text-theme-economic-ink underline-offset-4 hover:underline"
        >
          {t("tools.card.access")}
          <ArrowUpRight className="size-4" aria-hidden />
        </a>
        <button
          type="button"
          aria-pressed={tool.is_favorite}
          aria-label={
            tool.is_favorite
              ? t("tools.card.unfavorite")
              : t("tools.card.favorite")
          }
          disabled={isToggling}
          onClick={() => onToggleFavorite(tool)}
          className={cn(
            "grid size-9 place-items-center rounded-lg border transition-colors",
            tool.is_favorite
              ? "border-destructive/30 bg-destructive/10"
              : "border-border bg-background hover:bg-accent",
            isToggling && "opacity-60",
          )}
        >
          <Heart
            className={cn(
              "size-[18px]",
              tool.is_favorite
                ? "fill-destructive text-destructive"
                : "text-muted-foreground",
            )}
            aria-hidden
          />
        </button>
      </div>
    </Card>
  );
}
